import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import * as AgricultureIconModule from "@mui/icons-material/Agriculture.js";
import * as WaterDropIconModule from "@mui/icons-material/WaterDrop.js";
import * as LocalShippingIconModule from "@mui/icons-material/LocalShipping.js";
import * as SpeedIconModule from "@mui/icons-material/Speed.js";
import * as CategoryIconModule from "@mui/icons-material/Category.js";
import Page from "../Components/Page";
import MainButton from "../Components/Buttons/MainButton";
import DeleteResourceModal from "../Components/DeleteResourceModal";
import VersionHistoryControl, {
  HistoryViewingBanner,
} from "../Components/VersionHistoryControl";
import { useAuth } from "../Components/Auth/useAuth";
import useVersionHistory from "../hooks/useVersionHistory";
import { axiosInstance, buildLegacyHeaders } from "../Components/axiosFetchers";
import { buildSparsePayload, toOptionalNumber } from "../utils/sparsePayload";
import { useReferenceData } from "../hooks/useReferenceData";
import { hasComparableFormChanges } from "../utils/formComparison";
import { duplicateNameMessage } from "../utils/apiErrors.js";
import {
  GrowerToolbar,
  WorkspaceState,
} from "../Components/GrowerLayout.jsx";
import { trackEvent } from "../analytics/ga4.js";

const MACHINE_BASE_PATH = "/equipment";
const AgricultureIcon = AgricultureIconModule.default;
const WaterDropIcon = WaterDropIconModule.default;
const LocalShippingIcon = LocalShippingIconModule.default;
const SpeedIcon = SpeedIconModule.default;
const CategoryIcon = CategoryIconModule.default;

function getMachineIcon(type) {
  switch (type) {
    case "SPRAYER":
      return WaterDropIcon;
    case "COMBINE":
      return LocalShippingIcon;
    case "PLANTER":
      return AgricultureIcon;
    case "SWATHER":
      return AgricultureIcon;
    case "TRACTOR":
    default:
      return AgricultureIcon;
  }
}

function normalizeMachineForm(machine) {
  return {
    farm_id: machine?.farm_id ?? "",
    type: machine?.type || "",
    name: machine?.name || "",
    horsepower:
      machine?.horsepower === 0 || machine?.horsepower ? machine.horsepower : "",
    default_acre_hour:
      machine?.default_acre_hour === 0 || machine?.default_acre_hour
        ? machine.default_acre_hour
        : "",
  };
}

function buildMachinePayload(machine) {
  return buildSparsePayload({
    farm_id: machine?.farm_id === "" || machine?.farm_id == null ? null : Number(machine.farm_id),
    type: machine?.type,
    name: machine?.name,
    horsepower: toOptionalNumber(machine?.horsepower),
    default_acre_hour: toOptionalNumber(machine?.default_acre_hour),
  });
}

function upsertById(items, nextItem) {
  const withoutCurrent = items.filter((item) => item.id !== nextItem.id);
  return [...withoutCurrent, nextItem];
}

function formatDate(value) {
  if (!value) {
    return "Not available";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Not available";
  }

  return parsed.toLocaleDateString();
}

function EquipmentListCard({ machine, machineTypeLabels, farmName }) {
  const Icon = getMachineIcon(machine.type);

  return (
    <Link
      className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_18px_45px_rgba(15,23,42,0.05)] transition hover:-translate-y-0.5 hover:shadow-[0_22px_55px_rgba(15,23,42,0.08)]"
      to={`${MACHINE_BASE_PATH}/${machine.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-[8px] bg-[#eef7fb] text-[#243245]">
          <Icon sx={{ fontSize: 28 }} />
        </div>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[12px] font-bold uppercase tracking-[0.16em] text-slate-500">
          {machineTypeLabels[machine.type] || machine.type || "Equipment"}
        </span>
      </div>
      <h3 className="mt-5 text-[24px] font-black text-slate-900">
        {machine.name || "Untitled machine"}
      </h3>
      <p className="mt-2 text-sm text-slate-600">{farmName || "Shared across farms"}</p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <div className="rounded-[8px] border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
            Horsepower
          </p>
          <p className="mt-2 text-lg font-bold text-slate-900">
            {machine.horsepower || "Not set"}
          </p>
        </div>
        <div className="rounded-[8px] border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
            Default ac/hr
          </p>
          <p className="mt-2 text-lg font-bold text-slate-900">
            {machine.default_acre_hour || "Not set"}
          </p>
        </div>
      </div>
    </Link>
  );
}

function EquipmentDetailPage({
  formState,
  historyBanner = null,
  historyControl = null,
  isSaveDisabled = false,
  isReadOnly = false,
  loading,
  machine,
  machineId,
  machineTypeOptions,
  farms,
  onChange,
  onDelete,
  onSave,
  saveState,
  setDeleteOpen,
}) {
  const navigate = useNavigate();
  const isNew = machineId === "new";
  const machineTypeLabels = Object.fromEntries(
    machineTypeOptions.map((option) => [option.value, option.label])
  );
  const Icon = getMachineIcon(formState.type || machine?.type || "TRACTOR");

  return (
    <div className="min-h-full w-full bg-[#fbfbf8] px-4 py-5 md:px-8">
      <div className="mx-auto w-full max-w-[1200px]">
        <div className="rounded-[8px] border border-slate-200 bg-white shadow-[0_24px_60px_rgba(18,28,45,0.08)]">
          <section className="overflow-hidden rounded-t-[8px] border-b border-slate-200 bg-[linear-gradient(135deg,#eef7fb_0%,#ffffff_45%,#fdf6ef_100%)] px-6 py-7 md:px-10 md:py-9">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-[760px]">
                <div className="flex flex-wrap items-center gap-3 text-[12px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  <Link className="text-[#243245] hover:text-[#f15d22]" to={MACHINE_BASE_PATH}>
                    Equipment
                  </Link>
                  <span>&bull;</span>
                  <span>{isNew ? "New record" : "Detail view"}</span>
                </div>
                <h1 className="mt-5 text-[34px] font-black leading-tight text-slate-900 md:text-[56px]">
                  {isNew ? "Create Equipment" : machine?.name || "Equipment Detail"}
                </h1>
                <p className="mt-4 max-w-[680px] text-[18px] leading-8 text-slate-600">
                  Assign equipment to a farm or keep it shared so it can be reused across
                  crop year workflows without rebuilding the same machine in each form.
                </p>
                <div className="mt-6">{historyBanner}</div>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <MainButton
                    onClick={onSave}
                    text={saveState === "saving" ? "Saving..." : isNew ? "Create Equipment" : "Save Changes"}
                    disabled={
                      saveState === "saving" ||
                      loading ||
                      isReadOnly ||
                      isSaveDisabled
                    }
                  />
                  <MainButton
                    onClick={() => navigate(MACHINE_BASE_PATH)}
                    secondary={true}
                    text="Back to list"
                  />
                  {!isNew && !isReadOnly && (
                    <MainButton
                      destructive={true}
                      text="Delete"
                      onClick={() => setDeleteOpen(true)}
                    />
                  )}
                </div>
              </div>
              <div className="flex w-full max-w-[310px] shrink-0 flex-col gap-4">
                <div className="flex justify-end">{historyControl}</div>
                <div className="rounded-[8px] border border-slate-200 bg-white/80 px-6 py-6 shadow-[0_16px_30px_rgba(20,28,38,0.05)]">
                  <div className="flex h-16 w-16 items-center justify-center rounded-[8px] bg-[#eef7fb] text-[#243245]">
                    <Icon sx={{ fontSize: 34 }} />
                  </div>
                  <div className="mt-5 text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
                    Machine Type
                  </div>
                  <div className="mt-2 text-2xl font-black text-slate-900">
                    {machineTypeLabels[formState.type] || "Select a type"}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="grid gap-8 px-6 py-8 md:px-10 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="rounded-[8px] border border-slate-200 bg-white p-6">
              <h2 className="text-[22px] font-black text-slate-900">Equipment Details</h2>
              <div className="mt-6 grid gap-5 md:grid-cols-2">
                <TextField select label="Farm" disabled={isReadOnly} value={formState.farm_id} onChange={(event) => onChange("farm_id", event.target.value)}>
                  <MenuItem value="">Shared across farms</MenuItem>
                  {farms.map((farm) => <MenuItem key={farm.id} value={farm.id}>{farm.name}</MenuItem>)}
                  {formState.farm_id && !farms.some((farm) => farm.id === Number(formState.farm_id)) ? <MenuItem value={formState.farm_id}>Archived farm</MenuItem> : null}
                </TextField>
                <TextField
                  disabled={isReadOnly}
                  select
                  label="Machine Type"
                  value={formState.type}
                  onChange={(event) => onChange("type", event.target.value)}
                >
                  {machineTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  disabled={isReadOnly}
                  label="Machine Name"
                  value={formState.name}
                  onChange={(event) => onChange("name", event.target.value)}
                />
                <TextField
                  disabled={isReadOnly}
                  label="Horsepower"
                  type="number"
                  value={formState.horsepower}
                  onChange={(event) =>
                    onChange("horsepower", event.target.value)
                  }
                  InputProps={{ inputProps: { min: 0 } }}
                />
                <TextField
                  disabled={isReadOnly}
                  label="Default ac/hr"
                  type="number"
                  value={formState.default_acre_hour}
                  onChange={(event) =>
                    onChange("default_acre_hour", event.target.value)
                  }
                  InputProps={{ inputProps: { min: 0, step: 0.1 } }}
                />
              </div>
            </div>

            <div className="space-y-5">
              <div className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_16px_30px_rgba(20,28,38,0.05)]">
                <h3 className="text-lg font-black text-slate-900">Usage Notes</h3>
                <p className="mt-4 text-[15px] leading-7 text-slate-600">
                  Farm equipment and shared equipment are available
                  in planting, cultivation, fertilizer, pesticide, and harvest forms.
                </p>
              </div>
              {!isNew && (
                <div className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_16px_30px_rgba(20,28,38,0.05)]">
                  <h3 className="text-lg font-black text-slate-900">Record Info</h3>
                  <div className="mt-4 space-y-3 text-[15px] leading-7 text-slate-600">
                    <p>Owner: {machine?.owner_email || "Not returned"}</p>
                    <p>Created: {formatDate(machine?.created_at)}</p>
                    <p>Updated: {formatDate(machine?.updated_at)}</p>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default function EquipmentPage() {
  const { machineId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { referenceData } = useReferenceData(["machine_types"]);
  const [machines, setMachines] = useState([]);
  const [farms, setFarms] = useState([]);
  const [filterFarm, setFilterFarm] = useState("ALL");
  const [formState, setFormState] = useState(normalizeMachineForm());
  const [currentMachine, setCurrentMachine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("idle");
  const [pageError, setPageError] = useState("");
  const [filterType, setFilterType] = useState("ALL");
  const [sortBy, setSortBy] = useState("name");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const history = useVersionHistory({
    user,
    enabled: Boolean(machineId) && machineId !== "new",
    historyPath: machineId && machineId !== "new" ? `/machines/${machineId}` : "",
    currentData: currentMachine,
    onRestore: async (snapshot) => {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/machines/${machineId}`,
        buildMachinePayload(snapshot),
        {
          headers: buildLegacyHeaders(user),
        }
      );

      return response.data;
    },
    onRestoreSuccess: (savedMachine) => {
      setCurrentMachine(savedMachine);
      setFormState(normalizeMachineForm(savedMachine));
      setMachines((prevState) => upsertById(prevState, savedMachine));
    },
  });
  const displayedMachine = history.versionData || currentMachine;
  const displayedFormState = history.isViewingHistoricalVersion
    ? normalizeMachineForm(displayedMachine)
    : formState;

  document.title = "Equipment - Field To Market Canada";

  const machineTypeOptions = referenceData.machine_types || [];
  const defaultMachineType = machineTypeOptions[0]?.value || "";
  const machineTypeLabels = Object.fromEntries(
    machineTypeOptions.map((option) => [option.value, option.label])
  );

  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      setLoading(true);
      setPageError("");

      try {
        const [listResponse, farmsResponse] = await Promise.all([axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/machines`,
          {
            headers: buildLegacyHeaders(user),
          }
        ), axiosInstance.get(`${process.env.REACT_APP_API_URL}/farms`, { headers: buildLegacyHeaders(user) })]);

        if (!isMounted) {
          return;
        }

        const nextMachines = Array.isArray(listResponse.data)
          ? listResponse.data
          : [];
        setMachines(nextMachines);
        setFarms(Array.isArray(farmsResponse.data) ? farmsResponse.data : farmsResponse.data?.data || []);

        if (!machineId) {
          setCurrentMachine(null);
          setFormState(normalizeMachineForm());
          return;
        }

        if (machineId === "new") {
          setCurrentMachine(null);
          setFormState(
            normalizeMachineForm({
              type: defaultMachineType,
            })
          );
          return;
        }

        const detailResponse = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/machines/${machineId}`,
          {
            headers: buildLegacyHeaders(user),
          }
        );

        if (!isMounted) {
          return;
        }

        setCurrentMachine(detailResponse.data);
        setFormState(normalizeMachineForm(detailResponse.data));
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setPageError("We couldn't load the equipment library right now.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      isMounted = false;
    };
  }, [defaultMachineType, machineId, user]);

  const visibleMachines = useMemo(() => {
    const nextMachines = machines.filter((machine) => {
      if (filterFarm === "SHARED" && machine.farm_id != null) return false;
      if (!["ALL", "SHARED"].includes(filterFarm) && String(machine.farm_id) !== filterFarm) return false;
      if (filterType === "ALL") {
        return true;
      }

      return machine.type === filterType;
    });

    nextMachines.sort((left, right) => {
      if (sortBy === "recent") {
        return (
          new Date(right.updated_at || right.created_at || 0).getTime() -
          new Date(left.updated_at || left.created_at || 0).getTime()
        );
      }

      if (sortBy === "type") {
        return String(left.type || "").localeCompare(String(right.type || ""));
      }

      return String(left.name || "").localeCompare(String(right.name || ""));
    });

    return nextMachines;
  }, [filterType, filterFarm, machines, sortBy]);

  function handleFieldChange(key, value) {
    setFormState((prevState) => ({ ...prevState, [key]: value }));
    setSaveState("idle");
    setPageError("");
  }

  const savedMachineForm = useMemo(
    () =>
      normalizeMachineForm(
        currentMachine ||
          (machineId === "new"
            ? {
                type: defaultMachineType,
              }
            : null)
      ),
    [currentMachine, defaultMachineType, machineId]
  );
  const isSaveDisabled = useMemo(
    () =>
      !hasComparableFormChanges(
        buildMachinePayload(formState),
        buildMachinePayload(savedMachineForm)
      ),
    [formState, savedMachineForm]
  );

  async function handleSave() {
    setSaveState("saving");
    setPageError("");

    try {
      const payload = buildMachinePayload(formState);

      const response =
        machineId && machineId !== "new"
          ? await axiosInstance.patch(
              `${process.env.REACT_APP_API_URL}/machines/${machineId}`,
              payload,
              {
                headers: buildLegacyHeaders(user),
              }
            )
          : await axiosInstance.post(
              `${process.env.REACT_APP_API_URL}/machines`,
              payload,
              {
                headers: buildLegacyHeaders(user),
              }
            );

      const savedMachine = response.data;

      setMachines((prevState) => upsertById(prevState, savedMachine));
      setCurrentMachine(savedMachine);
      setFormState(normalizeMachineForm(savedMachine));
      trackEvent("equipment_saved", {
        workflow_area: "equipment",
        is_new_record: !machineId || machineId === "new",
        result: "success",
      });
      setSaveState("saved");

      if (!machineId || machineId === "new") {
        navigate(`${MACHINE_BASE_PATH}/${savedMachine.id}`, { replace: true });
      }
    } catch (error) {
      console.error(error);
      setSaveState("error");
      setPageError(
        duplicateNameMessage(error, {
          resourceLabel: "machine",
          name: formState.name,
          scope: "with this machine type",
          fallback: "We couldn't save this equipment record.",
        })
      );
    }
  }

  async function handleDelete() {
    if (!currentMachine?.id) {
      return;
    }

    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/machines/${currentMachine.id}`,
        {
          headers: buildLegacyHeaders(user),
        }
      );

      setMachines((prevState) =>
        prevState.filter((machine) => machine.id !== currentMachine.id)
      );
      setDeleteOpen(false);
      navigate(MACHINE_BASE_PATH);
    } catch (error) {
      console.error(error);
      setPageError("We couldn't delete this equipment record.");
      setDeleteOpen(false);
    }
  }

  if (machineId) {
    return (
      <Page
        title="Equipment"
        headerBorderColor="border-[#00ADEE]"
        headerVariant="hybrid"
        contentWidth="wide"
        padding="p-0"
        introContent={
          <p>
            Organize equipment by farm or share it across farms so crop year
            workflows can pull from one shared machine library.
          </p>
        }
      >
        <>
          <EquipmentDetailPage
            formState={displayedFormState}
            historyBanner={
              <HistoryViewingBanner
                onOpenHistory={history.openDialog}
                onRestore={history.restoreSelectedVersion}
                onShowCurrent={history.showCurrentVersion}
                onViewNewer={
                  history.canViewNewer
                    ? () => history.selectAdjacentVersion(-1)
                    : undefined
                }
                onViewOlder={
                  history.canViewOlder
                    ? () => history.selectAdjacentVersion(1)
                    : undefined
                }
                restoreDisabled={history.restoreState === "saving"}
                restoring={history.restoreState === "saving"}
                summary={history.activeSummary}
              />
            }
            historyControl={
              machineId !== "new" ? (
                <VersionHistoryControl
                  activeSummary={history.activeSummary}
                  canViewNewer={history.canViewNewer}
                  canViewOlder={history.canViewOlder}
                  detailState={history.detailState}
                  dialogOpen={history.dialogOpen}
                  errorMessage={history.errorMessage}
                  onClose={history.closeDialog}
                  onOpen={history.openDialog}
                  onRestore={history.restoreSelectedVersion}
                  onSelectVersion={history.selectVersion}
                  onShowCurrent={history.showCurrentVersion}
                  onViewNewer={
                    history.canViewNewer
                      ? () => history.selectAdjacentVersion(-1)
                      : undefined
                  }
                  onViewOlder={
                    history.canViewOlder
                      ? () => history.selectAdjacentVersion(1)
                      : undefined
                  }
                  resourceLabel="Machine"
                  restoreState={history.restoreState}
                  summaries={history.summaries}
                  summaryState={history.summaryState}
                  summaryTitle={(summary) =>
                    `Machine v${summary?.version_no || "-"}`
                  }
                />
              ) : null
            }
            isSaveDisabled={isSaveDisabled}
            isReadOnly={history.isViewingHistoricalVersion}
            loading={loading}
            machine={displayedMachine}
            machineId={machineId}
            machineTypeOptions={machineTypeOptions}
            farms={farms}
            onChange={handleFieldChange}
            onDelete={handleDelete}
            onSave={handleSave}
            saveState={saveState}
            setDeleteOpen={setDeleteOpen}
          />
          {pageError ? (
            <div className="px-8 pb-6 text-sm text-[#c62828]">{pageError}</div>
          ) : null}
          <DeleteResourceModal
            title="Delete Equipment"
            description={`Are you sure you wish to permanently delete the ${
              currentMachine?.name || "selected"
            } equipment record?`}
            open={deleteOpen}
            handleClose={setDeleteOpen}
            handleDelete={handleDelete}
            resourceId={currentMachine?.id}
          />
        </>
      </Page>
    );
  }

  return (
    <Page
      title="Equipment"
      headerBorderColor="border-[#00ADEE]"
      headerVariant="hybrid"
      contentWidth="wide"
      padding="px-4 pb-6 pt-5 md:px-6 md:pb-8"
      introContent={
        <p>
          Organize equipment by farm or share it across farms so crop year
          workflows can pull from one shared machine library.
        </p>
      }
      headerActions={
        <MainButton
          onClick={() => navigate(`${MACHINE_BASE_PATH}/new`)}
          text="Create Equipment"
        />
      }
    >
      <div className="w-full text-[#666666]">
        <section className="w-full">
          <GrowerToolbar>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="grid gap-4 md:grid-cols-3">
                <TextField select label="Farm" value={filterFarm} onChange={(event) => setFilterFarm(event.target.value)} sx={{ minWidth: 220 }}>
                  <MenuItem value="ALL">All farms</MenuItem>
                  <MenuItem value="SHARED">Shared across farms</MenuItem>
                  {farms.map((farm) => <MenuItem key={farm.id} value={String(farm.id)}>{farm.name}</MenuItem>)}
                </TextField>
                <TextField
                  select
                  label="Category"
                  value={filterType}
                  onChange={(event) => setFilterType(event.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem value="ALL">All equipment</MenuItem>
                  {machineTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Sort"
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="type">Machine type</MenuItem>
                  <MenuItem value="recent">Recently updated</MenuItem>
                </TextField>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                <span className="inline-flex items-center gap-2">
                  <CategoryIcon sx={{ fontSize: 18 }} />
                  {visibleMachines.length} visible
                </span>
                <span className="inline-flex items-center gap-2">
                  <SpeedIcon sx={{ fontSize: 18 }} />
                  Shared across crop years
                </span>
              </div>
            </div>
          </GrowerToolbar>

          {pageError ? (
            <p className="mt-4 text-sm text-[#c62828]">{pageError}</p>
          ) : null}

          {loading ? (
            <WorkspaceState className="mt-4" state="loading" title="Loading equipment records" />
          ) : visibleMachines.length === 0 ? (
            <WorkspaceState
              className="mt-4 border-b border-slate-200"
              description="Create a machine here once, then reuse it from planting, cultivation, fertilizer, pesticide, and harvest forms."
              title="No equipment records yet"
            />
          ) : (
            <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {visibleMachines.map((machine) => (
                <EquipmentListCard
                  key={machine.id}
                  machine={machine}
                  machineTypeLabels={machineTypeLabels}
                  farmName={farms.find((farm) => farm.id === machine.farm_id)?.name || (machine.farm_id ? "Archived farm" : "")}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </Page>
  );
}
